import { useContext, useEffect, useRef } from "react"
import { useStateWithCallback } from "../../hooks"
import { useAccountLink } from "../../api/mutations"
import { OnboardingScreenWrapper } from "./OnboardingScreenWrapper.tsx"
import { ButtonV2, Spacing, Text, FullScreenBlur } from "../../ui-components"
import ConnectBankSvg from "../../assets/onboarding-v2/connect-bank.svg"
import styled from "@emotion/native"
import { Platform, ScrollView } from "react-native"
import { PlaidContext } from "../../context/plaidContext"

type ConnectYourBankScreenUIProps = {
  hideSkippingAccount?: boolean
  onSkip?: () => void
  isSkippingAccount?: boolean
}

export function ConnectYourBankScreenUI({
  hideSkippingAccount,
  onSkip,
  isSkippingAccount,
}: ConnectYourBankScreenUIProps) {
  const [linkFinished, setLinkFinished] = useStateWithCallback<boolean>(false)
  const accountLinkMutation = useAccountLink()
  const {
    openLayer,
    isPlaidLoading,
    setIsPlaidLoading,
    openPlaid,
    isLayerEligible,
    startLayerSession,
    restart,
    fallbackLinkToken,
    isLayerSessionLoading,
    linkToken,
    signUpPhoneNumber,
  } = useContext(PlaidContext)

  // "Connect bank" tap while the initial Layer session is still loading
  // races a stale LAYER_READY against a fresh unsubmitted native handler
  // (the "Please submit Phone Number before opening Link" bug). Even with
  // the correlation gates in usePlaidLayer, the cleanest UX is to hide the
  // manual retry affordance until the current session has resolved.
  const buttonBusy = isPlaidLoading || !!isSkippingAccount || isLayerSessionLoading

  const skipAccount = () => {
    onSkip?.()
  }

  function renderBlurView() {
    if (accountLinkMutation.isPending || isPlaidLoading) {
      return <FullScreenBlur loadingText="Connecting to your bank..." />
    }

    return null
  }

  // Start Layer only on this step — phone auth prefetches the token earlier,
  // but opening Plaid over OTP is wrong.
  const lastStartedSessionKey = useRef<string | null>(null)
  useEffect(() => {
    if (!signUpPhoneNumber || !linkToken.linkToken) {
      return
    }
    if (fallbackLinkToken.linkToken) {
      return
    }
    const sessionKey = `${linkToken.linkToken}:${signUpPhoneNumber}`
    if (lastStartedSessionKey.current === sessionKey) {
      return
    }
    lastStartedSessionKey.current = sessionKey
    setIsPlaidLoading(true)
    startLayerSession()
  }, [
    signUpPhoneNumber,
    linkToken.linkToken,
    fallbackLinkToken.linkToken,
    startLayerSession,
    setIsPlaidLoading,
  ])

  useEffect(() => {
    if (isLayerEligible && !fallbackLinkToken.linkToken) {
      setIsPlaidLoading(true)
      queueMicrotask(() => {
        openLayer.current()
      })
      return
    }
    if (!fallbackLinkToken.linkToken) return
    setIsPlaidLoading(true)
    queueMicrotask(() => {
      openPlaid.current()
    })
  }, [openLayer, isLayerEligible, openPlaid, fallbackLinkToken, setIsPlaidLoading])

  return (
    <OnboardingScreenWrapper stepNumber={4}>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ flexGrow: 1 }}>
        <Spacing top={52} />
        <ConnectBankSvg style={{ margin: "0 auto", alignSelf: "center" }} />
        <Spacing top={52} />
        <Container>
          <UpperTextsWrapper>
            <Text type="h1" align="center">
              Link your bank
            </Text>
            <Spacing top={12} />
            <Text fontSize={20} lineHeight={32} color="gray900" align="center">
              SynchPay uses Plaid to securely connect your bank accounts
            </Text>
          </UpperTextsWrapper>
          <Spacing top={20} />
          {linkFinished && (
            <>
              <ProgressMessage>
                <Text type="body2" color="secondary">
                  We are linking your bank account. Please wait..
                </Text>
              </ProgressMessage>
              <ButtonsWrapper>
                <ButtonV2
                  title="Try again"
                  onPress={() => {
                    setLinkFinished(false, () => null)
                    setIsPlaidLoading(true)
                  }}
                  variant="blue2"
                  loading={accountLinkMutation.isPending || isPlaidLoading}
                />
                {hideSkippingAccount ? null : (
                  <ButtonV2
                    title="Skip"
                    variant="zumthor"
                    onPress={skipAccount}
                    loading={accountLinkMutation.isPending || isPlaidLoading}
                  />
                )}
              </ButtonsWrapper>
            </>
          )}
          <ButtonsWrapper>
            <ButtonV2
              title="Connect bank"
              loading={buttonBusy}
              variant="blue2"
              onPress={() => {
                if (buttonBusy) {
                  return
                }
                setIsPlaidLoading(true)
                restart()
              }}
            />
            {hideSkippingAccount ? null : (
              <ButtonV2 title="Skip" variant="zumthor" loading={buttonBusy} onPress={skipAccount} />
            )}
          </ButtonsWrapper>
        </Container>
      </ScrollView>
      {renderBlurView()}
    </OnboardingScreenWrapper>
  )
}

const Container = styled.View`
  flex: 1;
  justify-content: space-between;
  max-width: 500px;
  width: 100%;
  align-self: center;
`

const UpperTextsWrapper = styled.View`
  margin-left: 20px;
  margin-right: 20px;
`

const ProgressMessage = styled.View`
  align-items: center;
`

const ButtonsWrapper = styled.View`
  padding: ${() => (Platform.OS === "web" ? "0 20px 20px 20px" : "0 20px")};
  gap: 16px;
`
